import React from 'react';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { PassOrderPageData } from '../../../api';
import { useLanguage } from '../../../utils/GeneralUtils';
import { useAccountData } from '../../StorefrontThemeWrapper';
import { customerFacingPriceFormatter } from '../../../orderPage/utils';
import { withBookingFee } from '../../../orderPage/core/pricing';
import { isFixedScheduleMonthly } from '../../../managePassesPage/utils/passUtils';

interface SubscriptionCardProps {
  passOrderPage: PassOrderPageData;
}

export const SubscriptionCard = ({ passOrderPage }: SubscriptionCardProps) => {
  const { accountId, storefrontId } = useParams();
  const [searchParams] = useSearchParams();
  const account = useAccountData();
  const { t } = useTranslation();
  const language = useLanguage();

  const priceLabel =
    account && passOrderPage.price > 0
      ? customerFacingPriceFormatter(withBookingFee(passOrderPage.price, account), account)
      : '';

  const metaParts = isFixedScheduleMonthly(passOrderPage.pass_type)
    ? [priceLabel && `${priceLabel} ${t('manage_passes.buy.perMonth')}`].filter(Boolean)
    : [
        priceLabel,
        passOrderPage.num_punches != null
          ? t('storefront.punches', { count: passOrderPage.num_punches })
          : t('pass.unlimited_punches'),
        t('pass.validity_days_value', { count: passOrderPage.validity_days }),
      ].filter(Boolean);

  return (
    <Link
      to={{ pathname: `/pass/${accountId}/${passOrderPage.id}/${storefrontId || ''}`, search: searchParams.toString() }}
      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <Grid
        container
        alignItems="center"
        flexWrap="nowrap"
        sx={{
          px: 3,
          py: 3,
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          transition: 'background-color 0.15s',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}>
        <Grid sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              lineHeight: 1.3,
              overflowWrap: 'anywhere',
            }}>
            {passOrderPage.label?.[language]}
          </Typography>
        </Grid>
        <Grid sx={{ ml: 2, flexShrink: 0, textAlign: 'right' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', whiteSpace: 'nowrap' }}>
            {metaParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && ' · '}
                <bdi>{part}</bdi>
              </React.Fragment>
            ))}
          </Typography>
        </Grid>
      </Grid>
    </Link>
  );
};
